import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import {
    LogOut, TrendingUp, Building2, CircleDot, Pause, Check, ExternalLink,
    Mail, MailOpen, MessageSquare, MessageSquareQuote, CreditCard, Shield, Users, Plus, Trash2, Settings, Download, BarChart3,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { TwoFactorSettings } from '../components/TwoFactorSettings';
import { PasswordStrength, isPasswordValid } from '../components/PasswordStrength';
import { PlatformSiteSettings } from '../components/PlatformSiteSettings';
import { PlatformTestimonialsPanel } from '../components/PlatformTestimonialsPanel';
import { Modal } from '../lib/Modal';
import { PLAN_LABEL, PLANS } from '../lib/plans';
import { invalidateLivePlansCache, useLivePlans } from '../lib/useLivePlans';
import { invalidateSiteSettingsCache } from '../lib/useSiteSettings';
import { roleLabel } from '../lib/roleLabels';
import { canManagePlatformTeam } from '../lib/platformRoles';

const STATUS_LABEL = {
    active: 'Activ',
    trial: 'Trial',
    suspended: 'Suspendat',
    expired: 'Expirat',
    pending_payment: 'Plată în așteptare',
};

const STATUS_BADGE = {
    active: 'teal',
    trial: 'cyan',
    suspended: 'magenta',
    expired: 'muted',
    pending_payment: 'amber',
};

const PLAN_OPTIONS = PLANS.map((p) => p.id);

const TABS = [
    { id: 'statistics', label: 'Statistici', icon: TrendingUp },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'restaurants', label: 'Restaurante', icon: Building2 },
    { id: 'subscriptions', label: 'Abonamente', icon: CreditCard },
    { id: 'plans', label: 'Planuri', icon: CreditCard },
    { id: 'site', label: 'Site', icon: Settings },
    { id: 'messages', label: 'Mesaje', icon: MessageSquare },
    { id: 'testimonials', label: 'Testimoniale', icon: MessageSquareQuote },
    { id: 'security', label: 'Securitate', icon: Shield },
];

function formatDate(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('ro-RO', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

function RestaurantLoginCode({ code }) {
    if (!code) return <span className="admin-cell-muted">—</span>;
    return (
        <span className="admin-login-code" data-testid={`restaurant-login-code-${code}`}>
            {code}
        </span>
    );
}

function expiryStyle(iso) {
    if (!iso) return { color: 'var(--text-muted)' };
    const exp = new Date(iso);
    if (Number.isNaN(exp.getTime())) return { color: 'var(--text-muted)' };
    const days = Math.ceil((exp.getTime() - Date.now()) / 86400000);
    if (days < 0) return { color: 'var(--neon-magenta)', fontWeight: 600 };
    if (days <= 7) return { color: 'var(--amber)', fontWeight: 600 };
    return { color: 'var(--text-muted)' };
}

export default function SuperAdmin() {
    const { user, logout, enterRestaurant } = useAuth();
    const nav = useNavigate();
    const { plans: livePlans } = useLivePlans();
    const [tab, setTab] = useState('restaurants');
    const [stats, setStats] = useState(null);
    const [restaurants, setRestaurants] = useState([]);
    const [groupByChain, setGroupByChain] = useState(true);
    const [entering, setEntering] = useState(null);
    const [savingPlan, setSavingPlan] = useState(null);
    const [extendingId, setExtendingId] = useState(null);
    const [customDays, setCustomDays] = useState({});
    const [contactMessages, setContactMessages] = useState([]);
    const [expandedMsg, setExpandedMsg] = useState(null);
    const [msgFilter, setMsgFilter] = useState('all');
    const [platformStaff, setPlatformStaff] = useState([]);
    const [showAddStaff, setShowAddStaff] = useState(false);
    const [staffForm, setStaffForm] = useState({ name: '', email: '', password: '' });
    const [staffError, setStaffError] = useState('');
    const [staffSaving, setStaffSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [deleteSaving, setDeleteSaving] = useState(false);

    const canManageTeam = canManagePlatformTeam(user?.role);

    const tabs = useMemo(() => {
        const items = [...TABS];
        if (canManageTeam) items.push({ id: 'team', label: 'Echipă', icon: Users });
        return items;
    }, [canManageTeam]);

    const reload = () => {
        api.get('/admin/stats/overview').then((r) => setStats(r.data));
        api.get('/admin/restaurants').then((r) => setRestaurants(r.data.items));
        api.get('/admin/contact-messages').then((r) => setContactMessages(r.data.items)).catch(() => {});
        if (canManageTeam) {
            api.get('/admin/platform-staff').then((r) => setPlatformStaff(r.data.items)).catch(() => {});
        }
    };

    useEffect(() => { reload(); }, [canManageTeam]);

    const unreadCount = stats?.unread_contact_messages ?? 0;
    const pendingTestimonials = stats?.pending_testimonials ?? 0;

    const filteredMessages = useMemo(() => {
        if (msgFilter === 'unread') return contactMessages.filter((m) => !m.read);
        return contactMessages;
    }, [contactMessages, msgFilter]);

    const setStatus = async (rid, status) => {
        await api.put(`/admin/restaurants/${rid}/status`, { status });
        reload();
    };

    const setPlan = async (rid, plan) => {
        setSavingPlan(rid);
        try {
            await api.put(`/admin/restaurants/${rid}/plan`, { plan });
            setRestaurants((prev) => prev.map((r) => (r.id === rid ? { ...r, plan } : r)));
            api.get('/admin/stats/overview').then((r) => setStats(r.data));
        } catch (e) {
            alert(e?.response?.data?.detail || 'Nu am putut schimba planul');
        } finally {
            setSavingPlan(null);
        }
    };

    const extendSubscription = async (rid, days) => {
        const n = parseInt(days, 10);
        if (!Number.isFinite(n) || n <= 0 || n > 365) {
            alert('Zile invalide (1–365)');
            return;
        }
        setExtendingId(rid);
        try {
            const { data } = await api.post(`/admin/restaurants/${rid}/extend-subscription`, { days: n });
            setRestaurants((prev) => prev.map((r) => (r.id === rid
                ? { ...r, subscription_expires_at: data.subscription_expires_at, status: data.status }
                : r)));
            api.get('/admin/stats/overview').then((r) => setStats(r.data));
        } catch (e) {
            alert(e?.response?.data?.detail || 'Nu am putut extinde abonamentul');
        } finally {
            setExtendingId(null);
        }
    };

    const openRestaurant = async (rid) => {
        setEntering(rid);
        try {
            await enterRestaurant(rid, () => nav('/r'));
        } catch (e) {
            alert(e?.response?.data?.detail || e?.message || 'Nu am putut deschide restaurantul');
        } finally {
            setEntering(null);
        }
    };

    const handleLogout = async () => { await logout(); nav('/login', { replace: true, state: {} }); };

    const markContactRead = async (msgId) => {
        try {
            await api.put(`/admin/contact-messages/${msgId}/read`);
            setContactMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, read: true } : m)));
            api.get('/admin/stats/overview').then((r) => setStats(r.data));
        } catch (e) {
            alert(e?.response?.data?.detail || 'Nu am putut marca mesajul');
        }
    };

    const createPlatformStaff = async (e) => {
        e.preventDefault();
        setStaffError('');
        setStaffSaving(true);
        try {
            await api.post('/admin/platform-staff', staffForm);
            setStaffForm({ name: '', email: '', password: '' });
            setShowAddStaff(false);
            invalidateLivePlansCache();
            reload();
        } catch (err) {
            setStaffError(formatApiError(err));
        } finally {
            setStaffSaving(false);
        }
    };

    const toggleStaffActive = async (member) => {
        if (member.role === 'super_admin') return;
        try {
            await api.put(`/admin/platform-staff/${member.id}`, { active: member.active === false });
            invalidateLivePlansCache();
            reload();
        } catch (e) {
            alert(e?.response?.data?.detail || 'Nu am putut actualiza utilizatorul');
        }
    };

    const deletePlatformStaff = async (member) => {
        if (member.role === 'super_admin') return;
        if (!window.confirm(`Ștergi accesul pentru ${member.name}?\n\nVei fi rugat să confirmi cu parola ta.`)) return;
        const password = window.prompt('Introdu parola contului tău pentru a confirma ștergerea:');
        if (!password) return;
        try {
            await api.delete(`/admin/platform-staff/${member.id}`, { data: { password } });
            invalidateLivePlansCache();
            reload();
        } catch (e) {
            alert(e?.response?.data?.detail || 'Nu am putut șterge utilizatorul');
        }
    };

    const openDeleteRestaurant = (restaurant) => {
        setDeleteTarget(restaurant);
        setDeletePassword('');
        setDeleteError('');
    };

    const closeDeleteRestaurant = () => {
        if (deleteSaving) return;
        setDeleteTarget(null);
        setDeletePassword('');
        setDeleteError('');
    };

    const exportRestaurantGdpr = async (restaurant) => {
        try {
            // Fetch as blob using the axios-like API with raw response.
            const resp = await api.get(`/admin/restaurants/${restaurant.id}/export-gdpr`, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/json' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `gdpr-export-${restaurant.slug || restaurant.id}-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert('Export GDPR eșuat: ' + (err?.response?.data?.detail || err?.message || 'eroare necunoscută'));
        }
    };

    const [dbExporting, setDbExporting] = useState(false);
    const exportFullDatabase = async () => {
        if (dbExporting) return;
        setDbExporting(true);
        try {
            const resp = await api.get('/admin/export/database', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([resp.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `clickmeniu_db_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert('Export bază de date eșuat: ' + (err?.response?.data?.detail || err?.message || 'eroare necunoscută'));
        } finally {
            setDbExporting(false);
        }
    };

    const confirmDeleteRestaurant = async (e) => {
        e.preventDefault();
        if (!deleteTarget) return;
        setDeleteError('');
        setDeleteSaving(true);
        try {
            await api.delete(`/admin/restaurants/${deleteTarget.id}`, { data: { password: deletePassword } });
            setDeleteTarget(null);
            setDeletePassword('');
            invalidateLivePlansCache();
            reload();
        } catch (err) {
            setDeleteError(formatApiError(err));
        } finally {
            setDeleteSaving(false);
        }
    };

    return (
        <div className="admin-page grain" data-testid="super-admin">
            <div className="admin-top glass">
                <header className="admin-header">
                    <div className="container-x admin-header__inner">
                        <div className="admin-header__brand">
                            <Logo size="sm" to="/admin" />
                            <span className="badge badge-magenta">{canManageTeam ? 'Super Admin' : 'Admin platformă'}</span>
                        </div>
                        <div className="admin-header__user">
                            <div style={{ textAlign: 'right' }} data-testid="user-session-info">
                                <div style={{ fontWeight: 600, fontSize: '0.92rem' }} data-testid="user-name">{user?.name}</div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }} data-testid="user-role">{roleLabel(user?.role)}</div>
                            </div>
                            <button type="button" className="btn btn-ghost" onClick={handleLogout} data-testid="sa-logout">
                                <LogOut size={16} /> Logout
                            </button>
                        </div>
                    </div>
                </header>

                <div className="container-x admin-tabs-bar">
                    <div className={`admin-tabs${tabs.length > 5 ? ' admin-tabs--xwide' : tabs.length > 4 ? ' admin-tabs--wide' : ''}`} role="tablist" aria-label="Secțiuni admin">
                        {tabs.map(({ id, label, icon: Icon }) => (
                            <button
                                key={id}
                                type="button"
                                role="tab"
                                aria-selected={tab === id}
                                className={`admin-tabs__btn${tab === id ? ' admin-tabs__btn--active' : ''}`}
                                onClick={() => setTab(id)}
                                data-testid={`sa-tab-${id}`}
                            >
                                <Icon size={15} />
                                {label}
                                {id === 'messages' && unreadCount > 0 && (
                                    <span className="admin-tabs__badge">{unreadCount}</span>
                                )}
                                {id === 'testimonials' && pendingTestimonials > 0 && (
                                    <span className="admin-tabs__badge">{pendingTestimonials}</span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="container-x admin-main">
                <div className="admin-shell">
                <div className="admin-intro">
                    <div className="kicker">{roleLabel(user?.role)}</div>
                    <h1 className="headline admin-intro__title">
                        Bună, <em style={{ color: 'var(--amber)' }}>{user?.name}</em>!
                    </h1>
                </div>

                <div className="admin-tab-content">
                {tab === 'statistics' && (
                    <div data-testid="sa-tab-panel-statistics">
                        <OverviewKpis stats={stats} unreadCount={unreadCount} />
                    </div>
                )}

                {tab === 'restaurants' && (
                    <div className="panel admin-panel" data-testid="sa-tab-panel-restaurants">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
                            <h3 className="headline admin-panel__title" style={{ margin: 0 }}>Restaurante înregistrate</h3>
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: 'var(--text-muted)', cursor: 'pointer' }} data-testid="group-chain-toggle">
                                <input
                                    type="checkbox"
                                    checked={groupByChain}
                                    onChange={(e) => setGroupByChain(e.target.checked)}
                                />
                                🔗 Grupează lanțurile (primary + locații sub el)
                            </label>
                        </div>
                        <div className="admin-table-wrap">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        {['Restaurant', 'Cod login', 'Email owner', 'Status', 'Creat', 'Expiră abonament', 'Acțiuni'].map((h) => (
                                            <th key={h}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        // Sort: primary of each chain first, then siblings under it. Non-chain items
                                        // mixed in at their normal position. Standalone restaurants by created_at.
                                        if (!groupByChain) return restaurants;
                                        const seen = new Set();
                                        const ordered = [];
                                        for (const r of restaurants) {
                                            if (seen.has(r.id)) continue;
                                            if (r.chain && r.chain.is_primary) {
                                                ordered.push(r);
                                                seen.add(r.id);
                                                const siblings = restaurants.filter(
                                                    (x) => x.chain && x.chain.primary_id === r.id && !x.chain.is_primary
                                                );
                                                siblings.forEach((s) => { ordered.push(s); seen.add(s.id); });
                                            } else if (r.chain && !r.chain.is_primary) {
                                                // Will be placed under its primary later. Skip for now.
                                                continue;
                                            } else {
                                                ordered.push(r);
                                                seen.add(r.id);
                                            }
                                        }
                                        // Any leftover (e.g. orphan secondary whose primary isn't in list) at the end.
                                        for (const r of restaurants) {
                                            if (!seen.has(r.id)) { ordered.push(r); seen.add(r.id); }
                                        }
                                        return ordered;
                                    })().map((r) => {
                                        const isChild = groupByChain && r.chain && !r.chain.is_primary;
                                        const isChainPrimary = groupByChain && r.chain && r.chain.is_primary;
                                        return (
                                        <tr key={r.id} data-testid={`sa-rest-${r.id}`}
                                            style={isChild ? { background: 'rgba(242,101,34,0.03)' } : undefined}>
                                            <td style={{ fontWeight: 600 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    {isChild && (
                                                        <span style={{ color: 'var(--text-faint)', fontSize: '1.1rem', lineHeight: 1 }} title="Sub-locație">└</span>
                                                    )}
                                                    <span>{r.name}</span>
                                                    {isChainPrimary && (
                                                        <span style={{
                                                            fontSize: '0.62rem', color: 'var(--amber)',
                                                            background: 'rgba(242,101,34,0.14)', padding: '1px 5px',
                                                            borderRadius: 3, fontWeight: 700, letterSpacing: '0.04em',
                                                        }} title="Locația principală a lanțului">PRIMARY</span>
                                                    )}
                                                    {r.chain && (
                                                        <span style={{
                                                            fontSize: '0.62rem', color: 'var(--text-muted)',
                                                            background: 'var(--surface-2)', padding: '1px 5px',
                                                            borderRadius: 3, fontWeight: 600,
                                                        }} title="Număr total locații în lanț">🔗 {r.chain.size}</span>
                                                    )}
                                                </div>
                                                <div className="admin-cell-sub">{r.slug}</div>
                                            </td>
                                            <td><RestaurantLoginCode code={r.staff_login_id} /></td>
                                            <td className="admin-cell-muted">
                                                {r.chain ? r.chain.owner_email : (r.admin_email || '—')}
                                            </td>
                                            <td>
                                                <span className={`badge badge-${STATUS_BADGE[r.status] || 'muted'}`}>
                                                    {STATUS_LABEL[r.status] || r.status}
                                                </span>
                                            </td>
                                            <td className="admin-cell-muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(r.created_at)}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                {(() => {
                                                    const exp = r.subscription_expires_at;
                                                    if (!exp) return <span className="admin-cell-muted">—</span>;
                                                    const expDate = new Date(exp);
                                                    const daysLeft = Math.ceil((expDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                                                    let color = 'var(--text-muted)';
                                                    let badge = null;
                                                    if (daysLeft < 0) {
                                                        color = 'var(--neon-magenta)';
                                                        badge = <span style={{ fontSize: '0.7rem', marginLeft: 6, color: 'var(--neon-magenta)' }}>expirat</span>;
                                                    } else if (daysLeft <= 5) {
                                                        color = 'var(--amber)';
                                                        badge = <span style={{ fontSize: '0.7rem', marginLeft: 6, color: 'var(--amber)' }}>{daysLeft}z</span>;
                                                    } else {
                                                        badge = <span style={{ fontSize: '0.7rem', marginLeft: 6, color: 'var(--text-faint)' }}>{daysLeft}z</span>;
                                                    }
                                                    return <span style={{ color }}>{formatDate(exp)}{badge}</span>;
                                                })()}
                                            </td>
                                            <td>
                                                <div className="admin-actions">
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm"
                                                        disabled={entering === r.id}
                                                        onClick={() => openRestaurant(r.id)}
                                                        data-testid={`sa-enter-${r.id}`}
                                                    >
                                                        {entering === r.id ? <span className="spinner" /> : <ExternalLink size={12} />}
                                                        Gestionează
                                                    </button>
                                                    {r.status !== 'active' && (
                                                        <button type="button"
                                                                className={r.status === 'pending_payment' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                                                                onClick={() => setStatus(r.id, 'active')}
                                                                data-testid={`sa-activate-${r.id}`}
                                                                title={r.status === 'pending_payment' ? 'Forțează activare (fără plată — pentru testare)' : 'Activează'}
                                                        >
                                                            {r.status === 'pending_payment' ? 'Forțează active' : 'Activează'}
                                                        </button>
                                                    )}
                                                    {r.status !== 'suspended' && (
                                                        <button type="button" className="btn btn-danger btn-sm" onClick={() => setStatus(r.id, 'suspended')} data-testid={`sa-suspend-${r.id}`} title="Suspendă">
                                                            <Pause size={12} />
                                                        </button>
                                                    )}
                                                    {r.status !== 'expired' && r.status !== 'suspended' && (
                                                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setStatus(r.id, 'expired')} data-testid={`sa-expire-${r.id}`} title="Marchează expirat (oprește abonamentul)">
                                                            ⏹
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary btn-sm"
                                                        onClick={() => exportRestaurantGdpr(r)}
                                                        data-testid={`sa-export-${r.id}`}
                                                        title="Export GDPR — descarcă toate datele restaurantului ca JSON (Art. 15 / Art. 20)"
                                                    >
                                                        <Download size={12} />
                                                    </button>
                                                    {canManageTeam && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => openDeleteRestaurant(r)}
                                                            data-testid={`sa-delete-${r.id}`}
                                                            title="Șterge definitiv toate datele (Art. 17 — dreptul de a fi uitat)"
                                                        >
                                                            <Trash2 size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {tab === 'subscriptions' && (
                    <div data-testid="sa-tab-panel-subscriptions">
                        <div className="admin-plan-grid">
                            <div className="admin-plan-stat panel">
                                <div className="admin-plan-stat__label">MRR estimat</div>
                                <div className="headline admin-plan-stat__value" style={{ color: 'var(--neon-magenta)' }}>
                                    {stats?.mrr ?? '—'} <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>RON</span>
                                </div>
                            </div>
                            {(stats?.plans_distribution || []).map((p) => (
                                <div key={p.plan} className="admin-plan-stat panel">
                                    <div className="admin-plan-stat__label">{PLAN_LABEL[p.plan] || p.plan}</div>
                                    <div className="headline admin-plan-stat__value">{p.count}</div>
                                </div>
                            ))}
                        </div>

                        <div className="panel admin-panel">
                            <h3 className="headline admin-panel__title">Abonamente restaurante</h3>
                            <div className="admin-table-wrap">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            {['Restaurant', 'Cod login', 'Plan', 'Status', 'Expiră', 'Preț/lună', 'Extinde sub.'].map((h) => (
                                                <th key={h}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {restaurants.map((r) => {
                                            const plan = livePlans.find((p) => p.id === (r.plan || 'standard'));
                                            return (
                                                <tr key={r.id}>
                                                    <td style={{ fontWeight: 600 }}>
                                                        <div>{r.name}</div>
                                                        <div className="admin-cell-sub">{r.admin_email || '—'}</div>
                                                    </td>
                                                    <td><RestaurantLoginCode code={r.staff_login_id} /></td>
                                                    <td>
                                                        <select
                                                            className="input"
                                                            value={r.plan || 'standard'}
                                                            disabled={savingPlan === r.id}
                                                            onChange={(e) => setPlan(r.id, e.target.value)}
                                                            data-testid={`sa-plan-${r.id}`}
                                                            style={{ width: '100%', maxWidth: 140, padding: '0.35rem 0.45rem', fontSize: '0.82rem' }}
                                                        >
                                                            {PLAN_OPTIONS.map((p) => (
                                                                <option key={p} value={p}>{PLAN_LABEL[p]}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td>
                                                        <span className={`badge badge-${r.status === 'active' ? 'teal' : r.status === 'suspended' ? 'magenta' : 'cyan'}`}>
                                                            {STATUS_LABEL[r.status] || r.status}
                                                        </span>
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap', ...expiryStyle(r.subscription_expires_at) }}>
                                                        {formatDate(r.subscription_expires_at)}
                                                    </td>
                                                    <td className="admin-cell-muted" style={{ whiteSpace: 'nowrap' }}>
                                                        {plan ? `${plan.priceRon} RON + TVA` : '—'}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary btn-sm"
                                                                disabled={extendingId === r.id}
                                                                onClick={() => extendSubscription(r.id, 15)}
                                                                data-testid={`sa-extend-15-${r.id}`}
                                                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.78rem' }}
                                                            >+15z</button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary btn-sm"
                                                                disabled={extendingId === r.id}
                                                                onClick={() => extendSubscription(r.id, 30)}
                                                                data-testid={`sa-extend-30-${r.id}`}
                                                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.78rem' }}
                                                            >+30z</button>
                                                            <input
                                                                type="number"
                                                                min={1}
                                                                max={365}
                                                                value={customDays[r.id] || ''}
                                                                onChange={(e) => setCustomDays((c) => ({ ...c, [r.id]: e.target.value }))}
                                                                placeholder="zile"
                                                                disabled={extendingId === r.id}
                                                                data-testid={`sa-extend-custom-${r.id}`}
                                                                style={{ width: 56, padding: '0.25rem 0.4rem', fontSize: '0.78rem', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)' }}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary btn-sm"
                                                                disabled={extendingId === r.id || !customDays[r.id]}
                                                                onClick={() => extendSubscription(r.id, customDays[r.id])}
                                                                data-testid={`sa-extend-apply-${r.id}`}
                                                                style={{ padding: '0.25rem 0.5rem', fontSize: '0.78rem' }}
                                                            >{extendingId === r.id ? '…' : 'OK'}</button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {tab === 'plans' && (
                    <div data-testid="sa-tab-panel-plans">
                        <PlanPricingPanel />
                    </div>
                )}

                {tab === 'analytics' && (
                    <div data-testid="sa-tab-panel-analytics">
                        <RevenueAnalyticsPanel />
                    </div>
                )}

                {tab === 'site' && (
                    <div data-testid="sa-tab-panel-site">
                        <PlatformSiteSettings />
                    </div>
                )}

                {tab === 'security' && (
                    <div className="panel admin-panel" data-testid="sa-tab-panel-security">
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', margin: '0 0 1rem', lineHeight: 1.5 }}>
                            Configurează autentificarea în doi pași (2FA) pentru contul tău de platformă.
                        </p>
                        <TwoFactorSettings />

                        <div className="panel" style={{ maxWidth: 640, marginTop: '1.4rem' }} data-testid="sa-db-export">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.6rem' }}>
                                <Download size={18} color="var(--amber)" />
                                <h2 className="headline" style={{ fontSize: '1.35rem', margin: 0 }}>
                                    Export bază de date
                                </h2>
                            </div>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: '0 0 1rem', lineHeight: 1.5 }}>
                                Descarcă întreaga bază de date ca fișier Excel (.xlsx) cu câte un sheet pentru fiecare colecție —
                                restaurante, comenzi, useri, plăți etc. Deschizi în Excel și folosești filtrele pe coloane.
                                Datele sensibile (parole, secrete API) sunt eliminate automat.
                            </p>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={exportFullDatabase}
                                disabled={dbExporting}
                                data-testid="sa-db-export-btn"
                            >
                                {dbExporting ? <><span className="spinner" /> Se generează…</> : <><Download size={14} /> Descarcă Excel</>}
                            </button>
                            <p style={{ color: 'var(--text-faint)', fontSize: '0.78rem', marginTop: '0.8rem', marginBottom: 0 }}>
                                Recomandat: descarcă o copie zilnic și păstreaz-o local sau pe un disk extern.
                            </p>
                        </div>
                    </div>
                )}

                {tab === 'messages' && (
                    <div className="panel admin-panel" data-testid="sa-contact-messages">
                        <div className="admin-panel__toolbar">
                            <div className="admin-filter-btns" style={{ marginLeft: 'auto' }}>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${msgFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                                    onClick={() => setMsgFilter('all')}
                                >
                                    Toate ({contactMessages.length})
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-sm ${msgFilter === 'unread' ? 'btn-primary' : 'btn-secondary'}`}
                                    onClick={() => setMsgFilter('unread')}
                                >
                                    Necitite ({unreadCount})
                                </button>
                            </div>
                        </div>

                        {filteredMessages.length === 0 ? (
                            <p style={{ color: 'var(--text-muted)', margin: 0 }}>
                                {msgFilter === 'unread' ? 'Niciun mesaj necitit.' : 'Niciun mesaj primit încă.'}
                            </p>
                        ) : (
                            <div className="admin-messages">
                                {filteredMessages.map((m) => {
                                    const open = expandedMsg === m.id;
                                    return (
                                        <div
                                            key={m.id}
                                            className={`admin-message ${m.read ? 'admin-message--read' : 'admin-message--unread'}`}
                                            data-testid={`sa-contact-${m.id}`}
                                        >
                                            <button
                                                type="button"
                                                className="admin-message__toggle"
                                                onClick={() => setExpandedMsg(open ? null : m.id)}
                                            >
                                                <div className="admin-message__meta">
                                                    <div className="admin-message__meta-row">
                                                        {!m.read && <span className="live-dot" />}
                                                        <span style={{ fontWeight: 600 }}>{m.name}</span>
                                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.84rem' }}>{m.email}</span>
                                                    </div>
                                                    <div className="admin-message__meta-sub">
                                                        {m.subject || 'Fără subiect'} · {formatDateTime(m.created_at)}
                                                    </div>
                                                </div>
                                                <span style={{ color: 'var(--text-faint)', fontSize: '0.8rem', flexShrink: 0 }}>
                                                    {open ? '▲' : '▼'}
                                                </span>
                                            </button>
                                            {open && (
                                                <div className="admin-message__body">
                                                    {m.phone && (
                                                        <div style={{ fontSize: '0.88rem', color: 'var(--text-muted)', marginTop: '0.75rem' }}>
                                                            Telefon: <strong style={{ color: 'var(--text)' }}>{m.phone}</strong>
                                                        </div>
                                                    )}
                                                    <p style={{ margin: '0.75rem 0 1rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{m.message}</p>
                                                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                        <a href={`mailto:${m.email}`} className="btn btn-secondary btn-sm">
                                                            <Mail size={12} /> Răspunde
                                                        </a>
                                                        {!m.read && (
                                                            <button type="button" className="btn btn-primary btn-sm" onClick={() => markContactRead(m.id)}>
                                                                <MailOpen size={12} /> Marchează citit
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {tab === 'testimonials' && (
                    <PlatformTestimonialsPanel onChange={() => api.get('/admin/stats/overview').then((r) => setStats(r.data))} />
                )}

                {tab === 'team' && canManageTeam && (
                    <div className="panel admin-panel" data-testid="sa-tab-panel-team">
                        <div className="admin-panel__toolbar">
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', margin: 0 }}>
                                Angajați cu acces la panoul Super Admin — restaurante, abonamente, mesaje.
                            </p>
                            <button type="button" className="btn btn-primary btn-sm" onClick={() => { setShowAddStaff((v) => !v); setStaffError(''); }}>
                                <Plus size={14} /> Adaugă utilizator
                            </button>
                        </div>

                        {showAddStaff && (
                            <form onSubmit={createPlatformStaff} className="panel" style={{ marginBottom: '1rem', padding: '1rem', background: 'var(--surface-2)' }}>
                                {staffError && (
                                    <div style={{ color: 'var(--neon-magenta)', fontSize: '0.88rem', marginBottom: '0.75rem' }}>{staffError}</div>
                                )}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                                    <div>
                                        <label className="label" htmlFor="staff-name">Nume</label>
                                        <input id="staff-name" className="input" required value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} placeholder="Nume Prenume" data-testid="sa-staff-name" />
                                    </div>
                                    <div>
                                        <label className="label" htmlFor="staff-email">Email</label>
                                        <input id="staff-email" className="input" type="email" required value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} placeholder="nume@clickmeniu.ro" data-testid="sa-staff-email" />
                                    </div>
                                    <div>
                                        <label className="label" htmlFor="staff-password">Parolă</label>
                                        <input id="staff-password" className="input" type="password" required value={staffForm.password} onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })} placeholder="••••••••" data-testid="sa-staff-password" />
                                        <PasswordStrength value={staffForm.password} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.85rem', flexWrap: 'wrap' }}>
                                    <button type="submit" className="btn btn-primary btn-sm" disabled={staffSaving || !isPasswordValid(staffForm.password)} data-testid="sa-staff-create">
                                        {staffSaving ? <span className="spinner" /> : 'Creează acces'}
                                    </button>
                                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowAddStaff(false)}>Anulează</button>
                                </div>
                            </form>
                        )}

                        <div className="admin-table-wrap">
                            <table className="admin-table">
                                <thead>
                                    <tr>{['Nume', 'Email', 'Rol', 'Status', 'Creat', 'Acțiuni'].map((h) => <th key={h}>{h}</th>)}</tr>
                                </thead>
                                <tbody>
                                    {platformStaff.map((m) => {
                                        const isOwner = m.role === 'super_admin';
                                        const isActive = m.active !== false;
                                        return (
                                            <tr key={m.id} data-testid={`sa-platform-staff-${m.id}`}>
                                                <td style={{ fontWeight: 600 }}>{m.name}</td>
                                                <td className="admin-cell-muted">{m.email}</td>
                                                <td><span className={`badge badge-${isOwner ? 'magenta' : 'cyan'}`}>{roleLabel(m.role)}</span></td>
                                                <td>
                                                    <span className={`badge badge-${isActive ? 'teal' : 'muted'}`}>{isActive ? 'Activ' : 'Dezactivat'}</span>
                                                </td>
                                                <td className="admin-cell-muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(m.created_at)}</td>
                                                <td>
                                                    {!isOwner ? (
                                                        <div className="admin-actions">
                                                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => toggleStaffActive(m)}>
                                                                {isActive ? 'Dezactivează' : 'Activează'}
                                                            </button>
                                                            <button type="button" className="btn btn-danger btn-sm" onClick={() => deletePlatformStaff(m)} data-testid={`sa-staff-delete-${m.id}`}>
                                                                <Trash2 size={12} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span style={{ color: 'var(--text-faint)', fontSize: '0.82rem' }}>Cont principal</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                </div>
                </div>
            </div>

            <Modal
                open={!!deleteTarget}
                onClose={closeDeleteRestaurant}
                title="Șterge restaurant"
                titleId="sa-delete-restaurant-title"
                maxWidth={480}
            >
                {deleteTarget && (
                    <form onSubmit={confirmDeleteRestaurant}>
                        <p style={{ color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 1rem' }}>
                            Ești sigur că vrei să ștergi definitiv <strong style={{ color: 'var(--text)' }}>{deleteTarget.name}</strong>?
                            Se vor șterge din baza de date restaurantul, utilizatorii, meniul, mesele, comenzile și toate datele asociate.
                            <strong style={{ color: 'var(--neon-magenta)', display: 'block', marginTop: '0.65rem' }}>
                                Acțiunea este ireversibilă.
                            </strong>
                        </p>
                        {deleteError && (
                            <div style={{ color: 'var(--neon-magenta)', fontSize: '0.88rem', marginBottom: '0.75rem' }}>{deleteError}</div>
                        )}
                        <div>
                            <label className="label" htmlFor="sa-delete-password">Confirmă cu parola ta de Super Admin</label>
                            <input
                                id="sa-delete-password"
                                className="input"
                                type="password"
                                required
                                autoFocus
                                value={deletePassword}
                                onChange={(e) => setDeletePassword(e.target.value)}
                                placeholder="Parola contului tău"
                                data-testid="sa-delete-password"
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                            <button type="submit" className="btn btn-danger btn-sm" disabled={deleteSaving || !deletePassword} data-testid="sa-delete-confirm">
                                {deleteSaving ? <span className="spinner" /> : 'Șterge definitiv'}
                            </button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={closeDeleteRestaurant} disabled={deleteSaving}>
                                Anulează
                            </button>
                        </div>
                    </form>
                )}
            </Modal>
        </div>
    );
}

function OverviewKpis({ stats, unreadCount }) {
    return (
        <div className="admin-kpi-grid admin-kpi-grid--tab">
            <Kpi icon={Building2} label="Total restaurante" value={stats?.total_restaurants ?? '—'} color="#F59E0B" testId="sa-total" />
            <Kpi icon={Check} label="Active" value={stats?.active_restaurants ?? '—'} color="#14F195" testId="sa-active" />
            <Kpi icon={CircleDot} label="În trial" value={stats?.trial_restaurants ?? '—'} color="#00D9FF" testId="sa-trial" />
            <Kpi icon={TrendingUp} label="MRR (RON)" value={stats?.mrr ?? '—'} color="#FF2E92" testId="sa-mrr" />
            <Kpi icon={Mail} label="Mesaje necitite" value={unreadCount} color="#00D9FF" testId="sa-contact-unread" />
        </div>
    );
}

function Kpi({ icon: Icon, label, value, color, testId }) {
    return (
        <div className="panel admin-kpi" data-testid={testId}>
            <div className="admin-kpi__head">
                <div className="kicker">{label}</div>
                <div className="admin-kpi__icon" style={{ background: `${color}1f`, border: `1px solid ${color}40` }}>
                    <Icon size={14} color={color} />
                </div>
            </div>
            <div className="headline admin-kpi__value">{value}</div>
        </div>
    );
}


// Super-admin plan pricing editor — set live prices + optional crossed-out "original" price
// Revenue analytics — MRR, churn, trial conversion, monthly revenue, top payers.
// Pulls a single endpoint so the dashboard is dirt cheap (one query, no chart lib).
function RevenueAnalyticsPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = () => {
        setLoading(true);
        setError('');
        api.get('/admin/stats/revenue')
            .then((r) => setData(r.data))
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    if (loading) {
        return (
            <div className="panel admin-panel" style={{ textAlign: 'center', padding: '3rem' }}>
                <div className="spinner" style={{ margin: '0 auto' }} />
            </div>
        );
    }
    if (error) {
        return (
            <div className="panel admin-panel" style={{ color: 'var(--neon-magenta)' }}>
                Eroare: {error}
                <button type="button" className="btn btn-secondary btn-sm" onClick={load} style={{ marginLeft: 12 }}>Reîncarcă</button>
            </div>
        );
    }
    if (!data) return null;

    const currency = data.currency || 'RON';
    const mrrTotal = data.mrr?.total || 0;
    const subs = data.subscribers || {};
    const months = data.revenue_last_6_months || [];
    const maxRevenue = Math.max(1, ...months.map((m) => m.revenue));

    return (
        <div data-testid="revenue-analytics">
            {/* 4 KPI cards — top row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 12, marginBottom: 18 }}>
                <KpiCard
                    label="MRR"
                    value={`${mrrTotal.toLocaleString('ro-RO')} ${currency}`}
                    hint={`${subs.active || 0} restaurante active`}
                    accent="var(--amber)"
                    testId="kpi-mrr"
                />
                <KpiCard
                    label="Conversie trial 30z"
                    value={`${data.trial_conversion_30d?.rate_pct ?? 0}%`}
                    hint={`${data.trial_conversion_30d?.converted ?? 0} / ${data.trial_conversion_30d?.expired_total ?? 0} converted`}
                    accent="var(--neon-teal)"
                    testId="kpi-conversion"
                />
                <KpiCard
                    label="Churn 30z"
                    value={`${data.churn_30d?.rate_pct ?? 0}%`}
                    hint={`${data.churn_30d?.count ?? 0} restaurante pierdute`}
                    accent={data.churn_30d?.rate_pct > 5 ? 'var(--neon-magenta)' : 'var(--text-muted)'}
                    testId="kpi-churn"
                />
                <KpiCard
                    label="Pipeline"
                    value={`${subs.trial || 0} în trial`}
                    hint={`${subs.expired || 0} expirate · ${subs.suspended || 0} suspendate`}
                    accent="var(--neon-cyan)"
                    testId="kpi-pipeline"
                />
            </div>

            {/* MRR breakdown by plan */}
            <div className="panel admin-panel" style={{ marginBottom: 18 }}>
                <h3 className="headline admin-panel__title">MRR pe planuri</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 12, marginTop: 12 }}>
                    {(data.mrr?.by_plan || []).map((p) => (
                        <div key={p.plan} className="panel" style={{ padding: '1rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <span className="kicker">{p.plan.toUpperCase()}</span>
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{p.count} restaurante</span>
                            </div>
                            <div style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--amber)', marginTop: 6 }}>
                                {p.mrr.toLocaleString('ro-RO')} <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{currency}/lună</span>
                            </div>
                            <div style={{ marginTop: 6, fontSize: '0.78rem', color: 'var(--text-faint)' }}>
                                {Math.round((p.mrr / Math.max(1, mrrTotal)) * 100)}% din MRR total
                            </div>
                        </div>
                    ))}
                    {(!data.mrr?.by_plan?.length) && (
                        <div style={{ color: 'var(--text-muted)', padding: '0.5rem' }}>Niciun abonament activ.</div>
                    )}
                </div>
            </div>

            {/* Revenue last 6 months — bar chart pur CSS, fără dep */}
            <div className="panel admin-panel" style={{ marginBottom: 18 }}>
                <h3 className="headline admin-panel__title">Venituri ultimele 6 luni</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0 0 1rem' }}>
                    Plăți confirmate per lună calendaristică (total {currency}, brut).
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${months.length || 1}, 1fr)`, gap: 12, alignItems: 'end', minHeight: 180 }}>
                    {months.map((m) => {
                        const ratio = m.revenue / maxRevenue;
                        return (
                            <div key={m.month} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                                <div style={{ fontSize: '0.78rem', color: 'var(--text)', fontWeight: 600 }}>
                                    {Math.round(m.revenue).toLocaleString('ro-RO')}
                                </div>
                                <div style={{
                                    width: '100%',
                                    height: `${Math.max(6, Math.round(ratio * 140))}px`,
                                    background: `linear-gradient(180deg, var(--amber) 0%, var(--amber-dark) 100%)`,
                                    borderRadius: '6px 6px 0 0',
                                    transition: 'height 0.3s',
                                    minHeight: 6,
                                }} />
                                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    {m.month}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: 'var(--text-faint)' }}>
                                    {m.payments} plăți
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Top 10 restaurants by total revenue */}
            <div className="panel admin-panel">
                <h3 className="headline admin-panel__title">Top 10 restaurante după venituri</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0 0 1rem' }}>
                    Sumă totală plăți confirmate lifetime.
                </p>
                {(!data.top_restaurants_by_revenue?.length) ? (
                    <div style={{ color: 'var(--text-muted)' }}>Nicio plată confirmată încă.</div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid var(--border-strong)', color: 'var(--text-muted)' }}>
                                    <th style={{ textAlign: 'left', padding: '0.5rem 0.6rem' }}>#</th>
                                    <th style={{ textAlign: 'left', padding: '0.5rem 0.6rem' }}>Restaurant</th>
                                    <th style={{ textAlign: 'left', padding: '0.5rem 0.6rem' }}>Plan</th>
                                    <th style={{ textAlign: 'right', padding: '0.5rem 0.6rem' }}>Total plătit</th>
                                    <th style={{ textAlign: 'right', padding: '0.5rem 0.6rem' }}>Plăți</th>
                                    <th style={{ textAlign: 'right', padding: '0.5rem 0.6rem' }}>Ultima plată</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.top_restaurants_by_revenue.map((r, i) => (
                                    <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                        <td style={{ padding: '0.55rem 0.6rem', color: 'var(--text-faint)' }}>{i + 1}</td>
                                        <td style={{ padding: '0.55rem 0.6rem', fontWeight: 600 }}>{r.name}</td>
                                        <td style={{ padding: '0.55rem 0.6rem' }}>
                                            <span className={`badge ${r.plan === 'pro' ? 'badge-amber' : 'badge-teal'}`}>
                                                {r.plan?.toUpperCase()}
                                            </span>
                                        </td>
                                        <td style={{ padding: '0.55rem 0.6rem', textAlign: 'right', fontWeight: 600, color: 'var(--amber)' }}>
                                            {r.total_paid_ron.toLocaleString('ro-RO')} {currency}
                                        </td>
                                        <td style={{ padding: '0.55rem 0.6rem', textAlign: 'right', color: 'var(--text-muted)' }}>{r.payments}</td>
                                        <td style={{ padding: '0.55rem 0.6rem', textAlign: 'right', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                            {r.last_paid_at ? new Date(r.last_paid_at).toLocaleDateString('ro-RO') : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function KpiCard({ label, value, hint, accent, testId }) {
    return (
        <div className="panel" data-testid={testId} style={{
            padding: '1rem 1.1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            borderLeft: `3px solid ${accent}`,
        }}>
            <span className="kicker" style={{ color: 'var(--text-muted)' }}>{label}</span>
            <span style={{ fontSize: '1.6rem', fontWeight: 700, color: accent }}>{value}</span>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-faint)' }}>{hint}</span>
        </div>
    );
}


// that shows up as discount on the public landing. Also edit per-cycle discounts.
function PlanPricingPanel() {
    const [items, setItems] = useState([]);
    const [cycles, setCycles] = useState([]);
    const [edits, setEdits] = useState({});      // { plan_id: { price_ron, original_price_ron } }
    const [cycleEdits, setCycleEdits] = useState({});  // { cycle_id: discount_percent }
    const [trialDays, setTrialDays] = useState(14);
    const [trialDirty, setTrialDirty] = useState(false);
    const [savingTrial, setSavingTrial] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const [savingCycle, setSavingCycle] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const reload = () => {
        api.get('/admin/plans/pricing').then((r) => {
            setItems(r.data.items || []);
            setCycles(r.data.cycles || []);
            const next = {};
            (r.data.items || []).forEach((p) => {
                next[p.id] = {
                    price_ron: p.price_ron,
                    original_price_ron: p.original_price_ron ?? '',
                };
            });
            setEdits(next);
            const c = {};
            (r.data.cycles || []).forEach((cy) => { c[cy.id] = cy.discount_percent; });
            setCycleEdits(c);
        });
        api.get('/admin/site-settings').then((r) => {
            const td = Number(r.data?.trial_days);
            setTrialDays(Number.isFinite(td) ? td : 14);
            setTrialDirty(false);
        }).catch(() => {});
    };
    useEffect(reload, []);

    const saveTrial = async () => {
        setError(''); setSuccess(''); setSavingTrial(true);
        const n = Math.max(0, Math.min(90, parseInt(trialDays, 10) || 0));
        try {
            await api.put('/admin/site-settings', { trial_days: n });
            invalidateSiteSettingsCache();
            setSuccess(n === 0
                ? 'Trial dezactivat — restaurantele noi se vor înregistra cu status expired.'
                : `Trial setat la ${n} zile pentru restaurantele noi.`);
            setTrialDirty(false);
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || 'Eroare salvare trial');
        } finally {
            setSavingTrial(false);
        }
    };

    const setCycleField = (cycleId, value) => {
        setCycleEdits((c) => ({ ...c, [cycleId]: value }));
    };

    const saveCycle = async (cycleId) => {
        setError(''); setSuccess(''); setSavingCycle(cycleId);
        const pct = Number(cycleEdits[cycleId]);
        if (!Number.isFinite(pct) || pct < 0 || pct > 90) {
            setError('Reducerea trebuie să fie între 0 și 90%');
            setSavingCycle(null);
            return;
        }
        try {
            await api.put(`/admin/plans/cycle-discount/${cycleId}`, { discount_percent: pct });
            const cycleLabel = cycles.find((c) => c.id === cycleId)?.label || cycleId;
            setSuccess(`Reducere ${cycleLabel}: ${pct}%`);
            invalidateLivePlansCache();
            reload();
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || 'Eroare salvare reducere');
        } finally {
            setSavingCycle(null);
        }
    };

    const resetCycle = async (cycleId) => {
        const cycleLabel = cycles.find((c) => c.id === cycleId)?.label || cycleId;
        if (!window.confirm(`Revii la reducerea default pentru ${cycleLabel}?`)) return;
        setError(''); setSuccess('');
        try {
            await api.delete(`/admin/plans/cycle-discount/${cycleId}`);
            setSuccess(`Reducere ${cycleLabel} revenită la default.`);
            invalidateLivePlansCache();
            reload();
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || 'Eroare reset');
        }
    };

    const setField = (planId, key, value) => {
        setEdits((e) => ({ ...e, [planId]: { ...e[planId], [key]: value } }));
    };

    const save = async (planId) => {
        setError(''); setSuccess(''); setSavingId(planId);
        const edit = edits[planId] || {};
        const payload = {
            price_ron: Number(edit.price_ron),
            original_price_ron: edit.original_price_ron === '' || edit.original_price_ron === null
                ? null : Number(edit.original_price_ron),
        };
        if (!Number.isFinite(payload.price_ron) || payload.price_ron < 0) {
            setError('Preț invalid');
            setSavingId(null);
            return;
        }
        try {
            await api.put(`/admin/plans/pricing/${planId}`, payload);
            setSuccess(`Salvat: ${planId.toUpperCase()} = ${payload.price_ron} RON`);
            invalidateLivePlansCache();
            reload();
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || 'Eroare salvare');
        } finally {
            setSavingId(null);
        }
    };

    const resetToDefault = async (planId) => {
        if (!window.confirm(`Revii la prețul default al planului ${planId.toUpperCase()}?`)) return;
        setError(''); setSuccess('');
        try {
            await api.delete(`/admin/plans/pricing/${planId}`);
            setSuccess(`Plan ${planId.toUpperCase()} revenit la default.`);
            invalidateLivePlansCache();
            reload();
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || 'Eroare reset');
        }
    };

    return (
        <div className="panel admin-panel" data-testid="plan-pricing-panel">
            <h3 className="headline admin-panel__title">Planuri & prețuri</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: 0 }}>
                Setezi prețul lunar (RON, fără TVA) al fiecărui plan și opțional „prețul tăiat"
                (afișat ca reducere pe landing). Prețurile se aplică instant la următoarea
                plată — fără re-deploy.
            </p>

            {error && <div style={{ color: 'var(--neon-magenta)', marginBottom: 10, fontSize: '0.9rem' }}>{error}</div>}
            {success && <div style={{ color: 'var(--neon-teal)', marginBottom: 10, fontSize: '0.9rem' }}>✓ {success}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: '1rem', marginTop: '1rem' }}>
                {items.map((p) => {
                    const edit = edits[p.id] || {};
                    const curPrice = Number(edit.price_ron) || 0;
                    const curOrig = edit.original_price_ron === '' || edit.original_price_ron == null
                        ? null : Number(edit.original_price_ron);
                    const discountPct = curOrig && curOrig > curPrice
                        ? Math.round(((curOrig - curPrice) / curOrig) * 100) : 0;
                    return (
                        <div key={p.id} className="panel" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div className="kicker">{p.name}</div>
                                    <div style={{ fontSize: '0.78rem', color: 'var(--text-faint)', marginTop: 2 }}>
                                        Default: {p.default_price_ron} RON
                                        {p.default_original_price_ron ? ` (tăiat: ${p.default_original_price_ron})` : ''}
                                    </div>
                                </div>
                                {p.override_active && <span className="badge badge-amber">override activ</span>}
                            </div>

                            <div>
                                <label className="label">Preț actual (RON, fără TVA)</label>
                                <input className="input" type="number" min="0" step="0.01"
                                       value={edit.price_ron ?? ''}
                                       onChange={(e) => setField(p.id, 'price_ron', e.target.value)}
                                       data-testid={`plan-price-${p.id}`} />
                            </div>

                            <div>
                                <label className="label">Preț „tăiat" / original (opțional, pentru reducere vizuală)</label>
                                <input className="input" type="number" min="0" step="0.01"
                                       value={edit.original_price_ron ?? ''}
                                       onChange={(e) => setField(p.id, 'original_price_ron', e.target.value)}
                                       placeholder="Lasă gol dacă nu vrei să afișezi reducere"
                                       data-testid={`plan-original-${p.id}`} />
                            </div>

                            {/* Live preview of how clients will see it on landing */}
                            <div style={{ padding: '0.8rem', background: 'var(--surface-2)', borderRadius: 8, textAlign: 'center' }}>
                                <div style={{ fontSize: '0.7rem', color: 'var(--text-faint)', marginBottom: 4 }}>VIZUALIZARE CLIENT</div>
                                {curOrig && curOrig > curPrice && (
                                    <span style={{ textDecoration: 'line-through', color: 'var(--text-faint)', marginRight: 8 }}>
                                        {curOrig.toFixed(0)} RON
                                    </span>
                                )}
                                <span style={{ color: 'var(--amber)', fontSize: '1.4rem', fontWeight: 700 }}>
                                    {curPrice.toFixed(0)} RON
                                </span>
                                {discountPct > 0 && (
                                    <span className="badge badge-magenta" style={{ marginLeft: 8 }}>−{discountPct}%</span>
                                )}
                                <div style={{ fontSize: '0.72rem', color: 'var(--text-faint)', marginTop: 4 }}>+ TVA · per lună</div>
                            </div>

                            <div style={{ display: 'flex', gap: 6 }}>
                                <button type="button" className="btn btn-primary btn-sm" style={{ flex: 1 }}
                                        disabled={savingId === p.id}
                                        onClick={() => save(p.id)}
                                        data-testid={`plan-save-${p.id}`}>
                                    {savingId === p.id ? <span className="spinner" /> : 'Salvează'}
                                </button>
                                {p.override_active && (
                                    <button type="button" className="btn btn-secondary btn-sm"
                                            onClick={() => resetToDefault(p.id)}
                                            data-testid={`plan-reset-${p.id}`}
                                            title="Șterge override-ul și revine la prețul din cod">
                                        Reset
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Cycle discounts — % off when client picks 3/6/12 months instead of monthly */}
            <h3 className="headline admin-panel__title" style={{ marginTop: '2rem' }}>Reduceri pe perioade</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: 0 }}>
                Pentru fiecare ciclu multi-lună, setezi reducerea procentuală aplicată peste prețul lunar
                (ex: 15% off pentru anual înseamnă 10 luni plătite din 12).
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))', gap: '1rem', marginTop: '1rem' }}>
                {cycles.filter((c) => c.editable).map((c) => {
                    const planStandard = items.find((p) => p.id === 'standard');
                    const planPro = items.find((p) => p.id === 'pro');
                    const pct = Number(cycleEdits[c.id] ?? c.discount_percent) || 0;
                    const previewMonth = (p) => p ? Math.round(p.price_ron * (1 - pct / 100)) : 0;
                    return (
                        <div key={c.id} className="panel" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div className="kicker">{c.label} ({c.months} luni)</div>
                                    <div style={{ fontSize: '0.78rem', color: 'var(--text-faint)', marginTop: 2 }}>
                                        Default: {c.default_discount_percent}%
                                    </div>
                                </div>
                                {c.override_active && <span className="badge badge-amber">override</span>}
                            </div>
                            <div>
                                <label className="label">Reducere (%, 0-90)</label>
                                <input className="input" type="number" min="0" max="90" step="1"
                                       value={cycleEdits[c.id] ?? ''}
                                       onChange={(e) => setCycleField(c.id, e.target.value)}
                                       data-testid={`cycle-discount-${c.id}`} />
                            </div>
                            <div style={{ padding: '0.7rem', background: 'var(--surface-2)', borderRadius: 8, fontSize: '0.85rem' }}>
                                <div style={{ fontSize: '0.68rem', color: 'var(--text-faint)', marginBottom: 6 }}>VIZUALIZARE</div>
                                {planStandard && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'var(--text-muted)' }}>Standard</span>
                                        <span style={{ color: 'var(--amber)', fontWeight: 600 }}>
                                            {previewMonth(planStandard)} RON/lună
                                        </span>
                                    </div>
                                )}
                                {planPro && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                                        <span style={{ color: 'var(--text-muted)' }}>Pro</span>
                                        <span style={{ color: 'var(--amber)', fontWeight: 600 }}>
                                            {previewMonth(planPro)} RON/lună
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <button type="button" className="btn btn-primary btn-sm" style={{ flex: 1 }}
                                        disabled={savingCycle === c.id}
                                        onClick={() => saveCycle(c.id)}
                                        data-testid={`cycle-save-${c.id}`}>
                                    {savingCycle === c.id ? <span className="spinner" /> : 'Salvează'}
                                </button>
                                {c.override_active && (
                                    <button type="button" className="btn btn-secondary btn-sm"
                                            onClick={() => resetCycle(c.id)}
                                            data-testid={`cycle-reset-${c.id}`}
                                            title="Șterge override-ul și revine la reducerea default">
                                        Reset
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Trial period — applies to every NEW restaurant signup. Changes here
                affect only future signups; existing restaurants keep their trial_ends_at. */}
            <h3 className="headline admin-panel__title" style={{ marginTop: '2rem' }}>Trial restaurante noi</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: 0 }}>
                Câte zile de acces gratuit primește orice restaurant nou la înregistrare.
                Schimbarea afectează doar restaurantele care se înregistrează DUPĂ salvare —
                cele existente păstrează data lor de expirare. Setează <strong>0</strong> ca să dezactivezi
                complet trial-ul (restaurantele noi vor avea status <code>expired</code> și nu vor putea folosi
                platforma până nu plătesc).
            </p>

            <div className="panel" style={{ display: 'grid', gridTemplateColumns: '180px 1fr auto', gap: 12, alignItems: 'end', marginTop: '1rem' }}>
                <div>
                    <label className="label">Zile trial (0–90)</label>
                    <input className="input" type="number" min={0} max={90} step={1}
                           value={trialDays}
                           onChange={(e) => {
                               const raw = e.target.value;
                               const n = raw === '' ? 0 : Math.max(0, Math.min(90, parseInt(raw, 10) || 0));
                               setTrialDays(n);
                               setTrialDirty(true);
                           }}
                           data-testid="sa-trial-days" />
                </div>
                <div style={{ padding: '0.75rem 0.9rem', background: 'var(--surface-2)', borderRadius: 8, fontSize: '0.86rem', color: 'var(--text-muted)' }}>
                    {trialDays === 0
                        ? <>Înregistrările noi vor primi status <code>expired</code> imediat — trebuie să plătească din prima.</>
                        : <>Restaurantele noi vor avea <strong style={{ color: 'var(--amber)' }}>{trialDays} zile</strong> gratuit cu toate funcțiile planului ales deblocate.</>
                    }
                </div>
                <button type="button" className="btn btn-primary"
                        disabled={savingTrial || !trialDirty}
                        onClick={saveTrial}
                        data-testid="sa-trial-save">
                    {savingTrial ? <span className="spinner" /> : 'Salvează'}
                </button>
            </div>
        </div>
    );
}
